import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NetworkVisualization from '@components/NetworkVisualization';
import leapYearLogo from '@assets/leapyearlogo.jpg';
import styles from './HomePage.module.css';

const rotatingPhrases = [
  'oil and gas field services',
  'industrial asset management',
  'power generation equipment',
  'telecommunication infrastructure',
];

interface NavigationCard {
  title: string;
  description: string;
  link: string;
}

const navigationCards: NavigationCard[] = [
  {
    title: 'Explore Features',
    description: 'Discover AI-powered tools built for industrial excellence and field operations.',
    link: '/product',
  },
  {
    title: 'What We Do',
    description: 'Learn how we help automate the maintenance of your assets in the field. From raw data to dispatching - we save you time.',
    link: '/resources',
  },
  {
    title: 'Request a Demo',
    description: 'See our platform in action and discover how we can transform your operations.',
    link: '/contact',
  },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentPhraseIndex((prev) => (prev + 1) % rotatingPhrases.length);
        setIsAnimating(false);
      }, 400);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className={styles.hero}>
        {/* Background Visualization */}
        <NetworkVisualization />

        {/* Hero Content */}
        <div className={styles.heroContainer}>
          {/* Backed by LeapYear Banner */}
          <a href="https://www.takeleapyear.com/" target="_blank" rel="noopener noreferrer" className={styles.backedByBanner}>
            <img src={leapYearLogo} alt="LeapYear" className={styles.backedByLogo} />
            <span className={styles.backedByText}>Backed by LeapYear</span>
          </a>

          <h1 className={styles.heroTitle}>
            <span className={styles.heroTitleLine}>AI assistance and insights for</span>
            <span className={styles.heroTitleLine}>
              <span className={`${styles.highlight} ${styles.rotatingText} ${isAnimating ? styles.animatingOut : styles.animatingIn}`}>
                {rotatingPhrases[currentPhraseIndex]}
              </span>
            </span>
          </h1>
          <p className={styles.heroSubtitle}>
            Empower your operations by streamlining dispatching, optimizing technician workflows, reducing truck roll, and increasing uptime.
          </p>
        </div>

        {/* Gradient fade at bottom */}
        <div className={styles.heroGradient}></div>
      </section>

      {/* Navigation Cards Section */}
      <section className={styles.navigationSection}>
        <div className={styles.navigationContainer}>
          <div className={styles.cardsGrid}>
            {navigationCards.map((card, index) => (
              <div
                key={card.title}
                className={styles.navCard}
                onClick={() => navigate(card.link)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Decorative elements */}
                <div className={styles.cardDecoration}>
                  <div className={styles.decorativeCircle}></div>
                  <div className={styles.decorativeGrid}></div>
                </div>

                {/* Card content */}
                <div className={styles.cardContent}>
                  <h3 className={styles.cardTitle}>{card.title}</h3>
                  <p className={styles.cardDescription}>{card.description}</p>
                  <div className={styles.cardArrow}>
                    <span>→</span>
                  </div>
                </div>

                {/* Hover effect overlay */}
                <div className={styles.cardHoverOverlay}></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
